import humanApiPowder from "@/assets/products/human-api-powder.jpg";
import yellowApiPowder from "@/assets/products/yellow-api-powder.jpg";
import veterinaryApiPowder from "@/assets/products/veterinary-api-powder.jpg";
import intermediatePowder from "@/assets/products/intermediate-powder.jpg";
import excipientPowder from "@/assets/products/excipient-powder.jpg";
import peptideVial from "@/assets/products/peptide-vial.jpg";
import researchChemical from "@/assets/products/research-chemical.jpg";
import injectableVial from "@/assets/products/injectable-vial.jpg";

// Legacy 1:1 path mapping. Old seeded rows in the DB stored the Vite source
// path (`/src/assets/products/X.jpg`) which doesn't exist in production. We
// translate those to the bundled import so historical rows keep rendering.
// New rows should use either:
//   - a `/products/X.jpg` absolute path (served from /public/products/), OR
//   - an https:// URL (Supabase Storage / DALL·E output) — passed through.
const PATH_MAP: Record<string, string> = {
  "/src/assets/products/human-api-powder.jpg": humanApiPowder,
  "/src/assets/products/yellow-api-powder.jpg": yellowApiPowder,
  "/src/assets/products/veterinary-api-powder.jpg": veterinaryApiPowder,
  "/src/assets/products/intermediate-powder.jpg": intermediatePowder,
  "/src/assets/products/excipient-powder.jpg": excipientPowder,
  "/src/assets/products/peptide-vial.jpg": peptideVial,
  "/src/assets/products/research-chemical.jpg": researchChemical,
  "/src/assets/products/injectable-vial.jpg": injectableVial,
};

// Per-category variant pool. Picking from a list (instead of a single
// default per category) means two products in the same category get
// visually distinct artwork — important when the catalogue grid puts
// 4 Intermediates side-by-side and the user would otherwise see the
// exact same jar four times in a row.
//
// The variants here are chosen so each one is *plausible* for its
// category: e.g. peptides get vial-style imagery; intermediates and
// generic powders get jar imagery. We deliberately don't shove the
// injectable vial into Intermediates even though that adds variety —
// the wrong visual context hurts trust more than visual sameness.
const CATEGORY_VARIANTS: Record<string, string[]> = {
  "Human APIs":         [humanApiPowder, yellowApiPowder, injectableVial],
  "Veterinary APIs":    [veterinaryApiPowder, yellowApiPowder, humanApiPowder],
  "Intermediates":      [intermediatePowder, humanApiPowder, yellowApiPowder],
  "Excipients":         [excipientPowder, humanApiPowder],
  "Peptides":           [peptideVial, injectableVial],
  "Nootropics":         [researchChemical, peptideVial],
  "Amino Acids":        [excipientPowder, humanApiPowder, yellowApiPowder],
  "Research Chemicals": [researchChemical, peptideVial],
};

// Hash a CAS number (or any string id) to a non-negative integer. We use
// digit-sum + djb2 mix — fast, deterministic, no crypto needed for what
// is essentially a stable variant picker.
const stableHash = (key: string): number => {
  let h = 5381;
  for (let i = 0; i < key.length; i++) {
    h = ((h << 5) + h + key.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

// Resolves the image to render for a product. Order of precedence:
//   1. External URL (https://… — typically an AI-generated cover stored in
//      Lovable / Supabase Storage). Pass straight through.
//   2. Internal bundled path that matches PATH_MAP (legacy rows).
//   3. Category variant picked deterministically by hashing the CAS — so
//      every product in the same category gets one of 2–3 plausible
//      images and renders the same way every time (stable for Google
//      Image cache / structured-data invalidation).
//   4. Generic powder fallback so the card never renders broken.
export function resolveProductImage(
  imageUrl?: string | null,
  category?: string,
  // Stable id (CAS number is ideal). Without it we fall back to the first
  // variant — older callers stay backwards-compatible.
  stableId?: string
): string {
  if (imageUrl) {
    // https://… — external (e.g. Supabase Storage / DALL·E output). Pass through.
    if (/^https?:\/\//i.test(imageUrl)) return imageUrl;
    // Legacy DB rows stored the bundler source path — translate.
    if (PATH_MAP[imageUrl]) return PATH_MAP[imageUrl];
    // Absolute public path (e.g. /products/X.jpg, served from /public/products/).
    // This is the new canonical format for DB-stored placeholder URLs.
    if (imageUrl.startsWith("/")) return imageUrl;
  }
  if (category && CATEGORY_VARIANTS[category]) {
    const variants = CATEGORY_VARIANTS[category];
    if (variants.length === 1) return variants[0];
    const idx = stableId ? stableHash(stableId) % variants.length : 0;
    return variants[idx];
  }
  return humanApiPowder;
}

// SEO-rich alt text. Built from product name, CAS, category, and a
// trust/grade phrase. Google Images, structured-data crawlers, and screen
// readers all consume this — so we pack in the long-tail buyer query
// ("amoxicillin trihydrate USP grade pharmaceutical API CAS 26787-78-0")
// without keyword stuffing.
//
// Example outputs:
//   "Amoxicillin Trihydrate — USP/EP pharmaceutical API · CAS 26787-78-0 · Human APIs"
//   "Semaglutide — research-grade peptide vial · CAS 910463-68-2 · Peptides"
export function altTextFor(
  name: string,
  category?: string,
  casNumber?: string
): string {
  const grade =
    category === 'Peptides' ? 'research-grade peptide vial'
    : category === 'Research Chemicals' ? 'research-grade reference compound'
    : category === 'Nootropics' ? 'research-grade nootropic compound'
    : category === 'Excipients' ? 'USP/EP/JP pharmaceutical excipient'
    : category === 'Amino Acids' ? 'USP/FCC nutritional-grade amino acid'
    : category === 'Veterinary APIs' ? 'veterinary-grade API powder'
    : category === 'Intermediates' ? 'GMP-grade pharmaceutical intermediate'
    : 'USP/EP pharmaceutical API';
  const parts = [name, '—', grade];
  if (casNumber) parts.push('· CAS', casNumber);
  if (category) parts.push('·', category);
  return parts.join(' ');
}
